import type GUI from 'lil-gui';
import { saveDefaultsToSource } from '../dev/saveDefaults';
import type { DefaultsFilePath, SaveDefaultsResult } from '../dev/saveDefaults';
import type { HydratedSceneAssembly, SceneLayerKind } from '../scene';
import {
  saveStyleConfigToSource,
  saveStyleToPreset,
  type StyleConfig,
} from '../render/StyleConfig';
import { readCameraTuning } from '../game/CameraConfig';
import { listSlidersFromGuis, type SliderInfo } from './SliderInventory';
import { StylePanel, type StylePanelOptions } from './StylePanel';
import { TuningPanel, type TuningTargets } from './TuningPanel';
import { createIconRail, type IconRail } from '../ui/IconRail';

export interface ScenePanelOptions {
  mode: 'game' | 'map' | 'rig';
  assembly?: HydratedSceneAssembly;
  style?: StylePanelOptions;
  tuning?: TuningTargets;
  extraContent?: HTMLElement[];
}

export interface LayerTabSpec {
  key: SceneLayerKind;
  label: string;
  icon?: string;
  file: DefaultsFilePath;
  data: unknown;
}

type RacerLayerKey = 'racer-mechanics' | 'racer-softbody' | 'racer-rigfx' | 'racer-paint';
const RACER_LAYER_KEYS: ReadonlySet<SceneLayerKind> = new Set<RacerLayerKey>([
  'racer-mechanics', 'racer-softbody', 'racer-rigfx', 'racer-paint',
]);

type ScenePanelSectionKey =
  | 'scene'
  | 'camera'
  | 'lighting'
  | 'sky'
  | 'track'
  | 'fx'
  | 'vehicle'
  | 'physics'
  | 'input'
  | 'performance'
  | 'layers';

const SCENE_PANEL_SECTIONS: ReadonlyArray<{
  key: ScenePanelSectionKey;
  label: string;
  icon: string;
  iconPath: string;
}> = [
  { key: 'scene', label: 'Scene', icon: 'SC', iconPath: '<path d="M4 21V5a2 2 0 0 1 2-2h8l6 6v12"/><path d="M14 3v6h6"/><path d="M8 14h8"/><path d="M8 18h5"/>' },
  { key: 'camera', label: 'Camera', icon: 'CA', iconPath: '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z"/><circle cx="12" cy="13" r="3"/>' },
  { key: 'lighting', label: 'Light', icon: 'LI', iconPath: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>' },
  { key: 'sky', label: 'Sky', icon: 'SK', iconPath: '<path d="M17.5 19H9a7 7 0 1 1 6.71-9"/><path d="M22 19h-6.5a3.5 3.5 0 1 1 0-7H16"/><path d="M5 19h1"/>' },
  { key: 'track', label: 'Track', icon: 'TR', iconPath: '<path d="M6 3c4 3 8 3 12 0"/><path d="M6 21c4-3 8-3 12 0"/><path d="M8 4.5 5 19.5"/><path d="m16 4.5 3 15"/><path d="M9.5 9h5"/><path d="M8.5 14h7"/>' },
  { key: 'fx', label: 'FX', icon: 'FX', iconPath: '<path d="M13 2 3 14h8l-1 8 10-12h-8z"/>' },
  { key: 'vehicle', label: 'Vehicle', icon: 'VE', iconPath: '<path d="M12 5v14"/><path d="M8 7c-2 1-3 3-3 5s1 4 3 5"/><path d="M16 7c2 1 3 3 3 5s-1 4-3 5"/><path d="M8 12h8"/>' },
  { key: 'physics', label: 'Physics', icon: 'PH', iconPath: '<path d="m21 16-9 5-9-5V8l9-5 9 5z"/><path d="m3.3 7.7 8.7 5 8.7-5"/><path d="M12 22V12"/>' },
  { key: 'input', label: 'Input', icon: 'IN', iconPath: '<path d="M6 11h4"/><path d="M8 9v4"/><path d="M15 12h.01"/><path d="M18 10h.01"/><path d="M17.3 5H6.7A4.7 4.7 0 0 0 2 9.7v4.6A4.7 4.7 0 0 0 6.7 19h10.6a4.7 4.7 0 0 0 4.7-4.7V9.7A4.7 4.7 0 0 0 17.3 5z"/>' },
  { key: 'performance', label: 'Perf', icon: 'PF', iconPath: '<path d="M3 12a9 9 0 0 1 18 0"/><path d="M12 12 7 7"/><path d="M6.3 18h11.4"/>' },
  { key: 'layers', label: 'Layers', icon: 'LA', iconPath: '<path d="m12 2 10 6-10 6L2 8z"/><path d="m2 12 10 6 10-6"/><path d="m2 16 10 6 10-6"/>' },
];

/**
 * Returns what the "Save layer" button should actually POST to disk for `spec`.
 *
 * `spec.data` is a snapshot of the assembly taken at panel-render time — it
 * does NOT reflect live slider edits. For layers that have a corresponding
 * live target (camera, handling, racer rig), splice the live values over
 * `spec.data` so Save Layer commits what the user sees, not the boot-time
 * values. Each racer template only owns a SUBSET of `PodRacerRigTuning` fields,
 * so we preserve that scope when splicing instead of dumping the full rig into
 * one template (which would clobber the merge order at boot).
 */
export function resolveLayerSavePayload(
  spec: LayerTabSpec,
  tuning: Partial<Pick<TuningTargets, 'camera' | 'physics' | 'racer'>> | undefined,
): unknown {
  if (!tuning || !spec.data || typeof spec.data !== 'object') return spec.data;
  const data = spec.data as Record<string, unknown>;

  if (spec.key === 'camera' && tuning.camera) {
    return { ...data, tuning: readCameraTuning(tuning.camera) };
  }
  if (spec.key === 'handling' && tuning.physics) {
    return { ...data, config: { ...tuning.physics } };
  }
  if (RACER_LAYER_KEYS.has(spec.key) && tuning.racer) {
    const live = tuning.racer.getPodRacerRigTuning() as unknown as Record<string, unknown>;
    const owned = (data.tuning ?? {}) as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const key of Object.keys(owned)) {
      next[key] = key in live ? live[key] : owned[key];
    }
    return { ...data, tuning: next };
  }
  return spec.data;
}

/**
 * Unified dev-control mount point for GAME / MAP / RIG.
 *
 * This first pass keeps the proven StylePanel and TuningPanel implementations
 * inside one owned surface so callers stop knowing which legacy panel they
 * are mounting. Follow-up layer-native tabs can replace internals here without
 * touching the three boot paths again.
 */
export class ScenePanel {
  readonly root: HTMLElement;
  readonly stylePanel: StylePanel | null = null;
  readonly tuningPanel: TuningPanel | null = null;
  private readonly opts: ScenePanelOptions;

  constructor(opts: ScenePanelOptions) {
    injectStyles();
    this.opts = opts;

    this.root = document.createElement('div');
    this.root.className = 'scene-panel';
    this.root.dataset.mode = opts.mode;

    if (opts.style) {
      this.stylePanel = new StylePanel(opts.style);
    }

    if (opts.tuning) {
      this.tuningPanel = new TuningPanel(opts.tuning, {
        includeVisualSections: !opts.style,
      });
    }

    if (opts.style && opts.tuning) {
      this.root.appendChild(this.buildUnifiedPanel());
    } else {
      if (opts.assembly && !opts.tuning) {
        this.root.appendChild(this.buildLayerTabs(opts.assembly));
      }
      if (this.stylePanel) {
        this.root.appendChild(this.stylePanel.root);
      }
      if (this.tuningPanel) {
        this.root.appendChild(this.tuningPanel.root);
      }
    }

    for (const node of opts.extraContent ?? []) {
      this.root.appendChild(node);
    }
  }

  getGuis(): GUI[] {
    const guis: GUI[] = [];
    if (this.stylePanel) guis.push(...this.stylePanel.getGuis());
    if (this.tuningPanel) guis.push(this.tuningPanel.getGui());
    return guis;
  }

  listSliders(): SliderInfo[] {
    return listSlidersFromGuis(this.getGuis());
  }

  destroy(): void {
    this.stylePanel?.destroy();
    this.tuningPanel?.destroy();
  }

  private buildUnifiedPanel(): HTMLElement {
    const root = document.createElement('section');
    root.className = 'scene-panel-unified';
    root.setAttribute('aria-label', 'Unified dev controls');

    const railHost = document.createElement('div');
    railHost.className = 'scene-panel-unified-rail-host';

    const body = document.createElement('div');
    body.className = 'scene-panel-unified-body';

    const panels = new Map<ScenePanelSectionKey, HTMLElement>();
    for (const section of SCENE_PANEL_SECTIONS) {
      const panel = document.createElement('section');
      panel.className = 'scene-panel-unified-section';
      panel.dataset.scenePanelSection = section.key;
      panel.setAttribute('aria-label', section.label);
      body.appendChild(panel);
      panels.set(section.key, panel);
    }

    const append = (key: ScenePanelSectionKey, node: HTMLElement | null | undefined): void => {
      if (!node) return;
      node.hidden = false;
      node.style.display = '';
      panels.get(key)?.appendChild(node);
    };

    const styleRoot = this.stylePanel?.root;
    if (styleRoot) {
      append('scene', styleRoot.querySelector<HTMLElement>('.style-panel-savebar'));
      append('scene', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="actions"]'));
      append('camera', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="camera"]'));
      append('lighting', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="lighting"]'));
      append('lighting', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="shadows"]'));
      append('sky', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="sky"]'));
      append('sky', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="day"]'));
      append('track', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="track"]'));
      append('track', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="ground"]'));
      append('fx', styleRoot.querySelector<HTMLElement>('.style-panel-tab-content[data-tab="sfx"]'));
    }

    const tuningRoot = this.tuningPanel?.root;
    if (tuningRoot) {
      this.removePlaceholderLayerCards(tuningRoot);
      this.moveTuningFolders(tuningRoot, 'style', ['Map Style Preset'], panels.get('scene'));
      this.moveTuningFolders(tuningRoot, 'style', ['Render Scale'], panels.get('performance'));
      this.moveTuningFolders(tuningRoot, 'style', ['Engine Trail'], panels.get('vehicle'));
      this.moveTuningFolders(tuningRoot, 'style', ['Pixelation'], panels.get('fx'));
      append('scene', tuningRoot.querySelector<HTMLElement>('[data-tuning-section-panel="physcam"]'));
      append('camera', tuningRoot.querySelector<HTMLElement>('[data-tuning-section-panel="camera"]'));
      append('physics', tuningRoot.querySelector<HTMLElement>('[data-tuning-section-panel="physics"]'));
      append('input', tuningRoot.querySelector<HTMLElement>('[data-tuning-section-panel="input"]'));
      append('vehicle', tuningRoot.querySelector<HTMLElement>('[data-tuning-section-panel="racer"]'));
      append('fx', tuningRoot.querySelector<HTMLElement>('[data-tuning-section-panel="tune"]'));
      const status = tuningRoot.querySelector<HTMLElement>('.tuning-panel-status');
      append('scene', status);
    }

    if (this.opts.assembly) {
      append('layers', this.buildLayerTabs(this.opts.assembly));
    }

    for (const [key, panel] of panels) {
      if (panel.children.length === 0) {
        panel.remove();
        panels.delete(key);
      }
    }

    for (const panel of panels.values()) {
      this.flattenRootTitles(panel);
    }

    const items = SCENE_PANEL_SECTIONS.filter((section) => panels.has(section.key));
    const fallbackKey = items[0]?.key ?? 'scene';
    const activeKey = panels.has(this.opts.mode === 'map' ? 'track' : 'scene')
      ? (this.opts.mode === 'map' ? 'track' : 'scene')
      : fallbackKey;
    let rail: IconRail<ScenePanelSectionKey>;
    rail = createIconRail<ScenePanelSectionKey>({
      ariaLabel: 'Dev control groups',
      className: 'scene-panel-unified-rail',
      itemClassName: 'scene-panel-unified-tab',
      items,
      activeKey,
      buttonRole: 'tab',
      dataAttribute: 'scene-panel-section-tab',
      onSelect: (key) => {
        rail.setActive(key);
        for (const [candidate, panel] of panels) {
          panel.hidden = candidate !== key;
        }
      },
    });

    railHost.appendChild(rail.root);
    root.append(railHost, body);
    rail.setActive(activeKey);
    for (const [candidate, panel] of panels) {
      panel.hidden = candidate !== activeKey;
    }
    return root;
  }

  /**
   * The sidebar icon rail already labels which section the user is viewing,
   * so a collapsible "Camera" title at the top of the Camera section is
   * redundant — and worse, it ships in the closed state on first open of
   * the tab. For each section, find every root-level lil-gui (whether it's
   * a StylePanel tab body, a TuningPanel section's inner gui, or a folder
   * moved over via moveTuningFolders), force it open, and:
   *   - When the section has exactly one root, hide its title entirely.
   *   - When it has multiple roots, keep titles but neutralize the
   *     collapse click so the content stays visible.
   * Nested folders (one level deeper) keep their normal collapse behavior.
   */
  private flattenRootTitles(panel: HTMLElement): void {
    const roots: HTMLElement[] = [];
    for (const child of Array.from(panel.children) as HTMLElement[]) {
      if (child.classList.contains('lil-gui') || child.classList.contains('style-panel-tab-content')) {
        roots.push(child);
        continue;
      }
      if (child.classList.contains('tuning-panel-section-panel')) {
        const inner = child.querySelector<HTMLElement>(':scope > .lil-gui');
        if (inner) roots.push(inner);
      }
    }
    if (roots.length === 0) return;
    for (const root of roots) {
      root.classList.remove('closed');
      const title = root.querySelector<HTMLElement>(':scope > .title');
      if (!title) continue;
      if (roots.length === 1) {
        title.style.display = 'none';
      } else {
        // Replace with a clone to drop lil-gui's click-to-close handler,
        // and tag it for static styling (no chevron, default cursor).
        const clone = title.cloneNode(true) as HTMLElement;
        clone.classList.add('scene-panel-unified-root-title-static');
        title.replaceWith(clone);
      }
    }
  }

  private moveTuningFolders(
    root: HTMLElement,
    panelKey: string,
    folderTitles: ReadonlyArray<string>,
    target: HTMLElement | undefined,
  ): void {
    if (!target) return;
    const panel = root.querySelector<HTMLElement>(`[data-tuning-section-panel="${panelKey}"]`);
    if (!panel) return;
    const wanted = new Set(folderTitles);
    for (const child of Array.from(panel.children) as HTMLElement[]) {
      const title = child.querySelector<HTMLElement>(':scope > .title')?.textContent?.trim();
      if (!title || !wanted.has(title)) continue;
      child.hidden = false;
      child.style.display = '';
      target.appendChild(child);
    }
  }

  private removePlaceholderLayerCards(root: HTMLElement): void {
    root.querySelectorAll('.tuning-panel-layer-save').forEach((node) => node.remove());
  }

  /** Mirror a successful scene-layer save into whatever file the runtime
   *  actually reads at boot for that layer. Returns null if the layer is
   *  read directly from the template (no mirror needed). */
  private async mirrorLayerSave(spec: LayerTabSpec): Promise<SaveDefaultsResult | null> {
    // Camera + handling: runtime boots from src/game/{Camera,Handling}.defaults.json.
    const data = spec.data as Record<string, unknown> | null;
    if (data && spec.key === 'camera' && 'tuning' in data) {
      return saveDefaultsToSource('src/game/Camera.defaults.json', data.tuning);
    }
    if (data && spec.key === 'handling' && 'config' in data) {
      return saveDefaultsToSource('src/game/Handling.defaults.json', data.config);
    }
    // Lighting / postfx / track: runtime hydrates StyleConfig from the bound
    // preset (or StyleConfig.defaults.json in fallback mode), NOT the scene
    // template — unless VITE_SCENE_LAYERS=1. Always mirror; it's a no-op when
    // the scene-layers flag is on (the file is identical) and the only thing
    // that makes save-layer take effect when it isn't.
    if (spec.key === 'lighting' || spec.key === 'postfx' || spec.key === 'track') {
      const target = this.resolveStyleSaveTarget();
      if (!target) return null;
      return target.isFallback || !target.presetName
        ? saveStyleConfigToSource(target.config)
        : saveStyleToPreset(target.presetName, target.config);
    }
    return null;
  }

  /** Look up the active StyleConfig + preset binding from whichever shape
   *  ScenePanel was constructed with (MAP/RIG pass `style`; GAME passes
   *  `tuning`). */
  private resolveStyleSaveTarget(): { config: StyleConfig; presetName: string; isFallback: boolean } | null {
    const style = this.opts.style;
    if (style) {
      return {
        config: style.config,
        presetName: style.preset?.name ?? '',
        isFallback: style.preset?.isFallback ?? true,
      };
    }
    const tuning = this.opts.tuning;
    if (tuning?.style && tuning.preset) {
      return {
        config: tuning.style,
        presetName: tuning.preset.activeName,
        isFallback: tuning.preset.isFallback,
      };
    }
    return null;
  }

  private buildLayerTabs(assembly: HydratedSceneAssembly): HTMLElement {
    const section = document.createElement('section');
    section.className = 'scene-layer-panel';
    section.setAttribute('aria-label', 'Scene layers');

    const body = document.createElement('div');
    body.className = 'scene-layer-body';

    const status = document.createElement('div');
    status.className = 'scene-layer-status';
    status.dataset.kind = 'info';

    const specs = layerSpecs(assembly);
    let active = specs[0];
    const tabs = createIconRail<SceneLayerKind>({
      ariaLabel: 'Scene layers',
      className: 'scene-layer-tabs',
      itemClassName: 'scene-layer-tab',
      items: specs.map((spec) => ({
        key: spec.key,
        label: spec.label,
        icon: spec.icon ?? spec.label.slice(0, 2).toUpperCase(),
      })),
      activeKey: active.key,
      buttonRole: 'tab',
      dataAttribute: 'scene-layer-tab',
      onSelect: (key) => {
        const next = specs.find((spec) => spec.key === key);
        if (!next) return;
        active = next;
        renderActive();
      },
    });

    const renderActive = (): void => {
      body.replaceChildren(this.buildLayerBody(active, status));
      tabs.setActive(active.key);
    };

    section.append(tabs.root, body, status);
    renderActive();
    return section;
  }

  private buildLayerBody(spec: LayerTabSpec, status: HTMLElement): HTMLElement {
    const root = document.createElement('div');
    root.className = 'scene-layer-editor';
    root.dataset.sceneLayer = spec.key;

    const title = document.createElement('div');
    title.className = 'scene-layer-title';
    title.textContent = spec.label;

    const meta = document.createElement('code');
    meta.className = 'scene-layer-file';
    meta.textContent = spec.file;

    const header = document.createElement('div');
    header.className = 'scene-layer-header';
    header.append(title, meta);

    const saveJson = document.createElement('details');
    saveJson.className = 'scene-layer-save-json';

    const saveJsonSummary = document.createElement('summary');
    saveJsonSummary.textContent = 'Save / JSON';

    const actions = document.createElement('div');
    actions.className = 'scene-layer-actions';

    const save = document.createElement('button');
    save.type = 'button';
    save.dataset.action = 'save-scene-layer';
    save.textContent = 'Save layer';
    save.addEventListener('click', () => {
      // Resolve at click time so live FollowCamera edits are captured. The
      // static `spec.data` is a render-time snapshot of the assembly and does
      // not reflect anything the user dragged a slider over.
      const payload = resolveLayerSavePayload(spec, this.opts.tuning);
      const liveSpec: LayerTabSpec = { ...spec, data: payload };
      void saveDefaultsToSource(spec.file, payload).then(async (result) => {
        if (!result.ok) {
          status.dataset.kind = 'error';
          status.textContent = `Save failed: ${result.error ?? 'unknown error'}`;
          return;
        }
        // Each scene-layer JSON has a parallel "what the runtime actually
        // reads on boot" target. Mirror the save there too so "Save layer"
        // is never a no-op, regardless of which mode the StyleConfig
        // pipeline is in (preset bindings vs. scene-layers flag).
        const mirror = await this.mirrorLayerSave(liveSpec);
        if (mirror && !mirror.ok) {
          status.dataset.kind = 'error';
          status.textContent = `Saved ${spec.label} but mirror failed: ${mirror.error ?? 'unknown error'}`;
          return;
        }
        status.dataset.kind = 'ok';
        status.textContent = mirror
          ? `Saved ${spec.label} + runtime mirror (${result.bytes ?? '?'}b)`
          : `Saved ${spec.label} (${result.bytes ?? '?'}b)`;
      });
    });

    const copy = document.createElement('button');
    copy.type = 'button';
    copy.dataset.action = 'copy-scene-layer-json';
    copy.textContent = 'Copy JSON';
    copy.addEventListener('click', () => {
      const payload = resolveLayerSavePayload(spec, this.opts.tuning);
      void copyText(JSON.stringify(payload, null, 2)).then(() => {
        status.dataset.kind = 'ok';
        status.textContent = `Copied ${spec.label} JSON`;
      }).catch((err) => {
        status.dataset.kind = 'error';
        status.textContent = `Copy failed: ${(err as Error).message}`;
      });
    });

    const json = document.createElement('pre');
    json.className = 'scene-layer-json';
    json.hidden = true;

    const view = document.createElement('button');
    view.type = 'button';
    view.dataset.action = 'view-scene-layer-json';
    view.textContent = 'View JSON';
    view.setAttribute('aria-expanded', 'false');
    view.addEventListener('click', () => {
      const expanded = json.hidden;
      if (expanded) {
        const payload = resolveLayerSavePayload(spec, this.opts.tuning);
        json.textContent = JSON.stringify(payload, null, 2);
      }
      json.hidden = !expanded;
      view.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      view.textContent = expanded ? 'Hide JSON' : 'View JSON';
    });

    actions.append(save, copy, view);
    saveJson.append(saveJsonSummary, actions, json);
    root.append(header, saveJson);
    return root;
  }
}

function layerSpecs(assembly: HydratedSceneAssembly): LayerTabSpec[] {
  const { layers } = assembly;
  return [
    { key: 'lighting', label: 'Lighting', icon: 'LI', file: `src/scene/templates/lighting/${layers.lighting.id}.json`, data: layers.lighting },
    { key: 'postfx', label: 'PostFx', icon: 'PX', file: `src/scene/templates/postfx/${layers.postFx.id}.json`, data: layers.postFx },
    { key: 'track', label: 'Track', icon: 'TR', file: `src/scene/templates/track/${layers.track.id}.json`, data: layers.track },
    { key: 'particles', label: 'Particles', icon: 'PA', file: `src/scene/templates/particles/${layers.particles.id}.json`, data: layers.particles },
    { key: 'camera', label: 'Camera', icon: 'CA', file: `src/scene/templates/camera/${layers.camera.id}.json`, data: layers.camera },
    { key: 'handling', label: 'Handling', icon: 'HA', file: `src/scene/templates/handling/${layers.handling.id}.json`, data: layers.handling },
    { key: 'racer', label: 'Racer', icon: 'RC', file: `src/scene/templates/racer/${layers.racer.id}.json`, data: layers.racer },
    { key: 'racer-mechanics', label: 'Mechanics', icon: 'ME', file: `src/scene/templates/racer-mechanics/${layers.racerMechanics.id}.json`, data: layers.racerMechanics },
    { key: 'racer-softbody', label: 'SoftBody', icon: 'SB', file: `src/scene/templates/racer-softbody/${layers.racerSoftBody.id}.json`, data: layers.racerSoftBody },
    { key: 'racer-rigfx', label: 'RigFx', icon: 'RF', file: `src/scene/templates/racer-rigfx/${layers.racerRigFx.id}.json`, data: layers.racerRigFx },
    { key: 'racer-paint', label: 'Paint', icon: 'PT', file: `src/scene/templates/racer-paint/${layers.racerPaint.id}.json`, data: layers.racerPaint },
  ];
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('clipboard unavailable');
}


function injectStyles(): void {
  if (document.getElementById('scene-panel-styles')) return;
  const style = document.createElement('style');
  style.id = 'scene-panel-styles';
  style.textContent = `
    .scene-panel {
      display: grid;
      gap: 10px;
      min-width: 0;
    }
    .scene-panel > * {
      min-width: 0;
    }
    .scene-panel-unified {
      display: grid;
      grid-template-columns: 58px minmax(0, 1fr);
      min-width: 0;
      min-height: 0;
      border: 1px solid rgba(126, 211, 255, 0.18);
      background: rgba(3, 8, 14, 0.62);
    }
    .scene-panel-unified-rail-host {
      min-width: 0;
      border-right: 1px solid rgba(126, 211, 255, 0.14);
      background: rgba(2, 7, 14, 0.36);
    }
    .scene-panel-unified-rail {
      padding: 8px 6px;
      gap: 6px;
    }
    .scene-panel-unified-tab.dev-icon-rail-button {
      width: 44px;
      min-width: 44px;
      min-height: 42px;
      border-color: rgba(126, 211, 255, 0.22);
      background: rgba(2, 10, 18, 0.72);
      color: rgba(217, 243, 255, 0.78);
    }
    .scene-panel-unified-tab .dev-icon-rail-glyph {
      width: 19px;
      height: 19px;
    }
    .scene-panel-unified-tab.is-active {
      border-color: rgba(89, 220, 255, 0.86);
      color: #59dcff;
      background: rgba(5, 24, 34, 0.92);
      box-shadow: inset 0 0 0 1px rgba(89, 220, 255, 0.14);
    }
    .scene-panel-unified-body {
      min-width: 0;
      overflow: auto;
    }
    .scene-panel-unified-section {
      min-width: 0;
    }
    .scene-panel-unified-section[hidden] {
      display: none;
    }
    .scene-panel-unified-section > .style-panel-tab-content,
    .scene-panel-unified-section > .tuning-panel-section-panel {
      display: block !important;
      width: 100% !important;
      min-width: 0;
    }
    .scene-panel-unified-section > .style-panel-tab-content > .title,
    .scene-panel-unified-section > .tuning-panel-section-panel > .lil-gui > .title,
    .scene-panel-unified-section > .lil-gui > .title {
      min-height: 36px;
      background: rgba(255, 255, 255, 0.03);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      color: rgba(248, 251, 255, 0.92);
      font: 800 12px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
      letter-spacing: 0;
      text-transform: none;
    }
    .scene-panel-unified-section .scene-panel-unified-root-title-static {
      cursor: default;
      pointer-events: none;
    }
    .scene-panel-unified-section .scene-panel-unified-root-title-static::before {
      content: none !important;
    }
    .scene-panel-unified-section .style-panel-savebar {
      grid-column: auto;
      grid-row: auto;
    }
    .scene-panel-unified-section .style-panel-tab-content,
    .scene-panel-unified-section .lil-gui.root,
    .scene-panel-unified-section .lil-gui {
      border-left: 0;
      border-right: 0;
      width: 100% !important;
    }
    .scene-layer-panel {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      grid-template-rows: auto auto;
      gap: 8px;
      padding: 8px;
      border: 1px solid rgba(126, 211, 255, 0.18);
      background: rgba(3, 8, 14, 0.62);
      align-items: start;
    }
    .scene-layer-tabs {
      grid-column: 1;
      grid-row: 1 / span 2;
      padding: 0 6px 0 0;
      border-right: 1px solid rgba(126, 211, 255, 0.14);
      align-self: start;
      max-height: 238px;
      overflow-y: auto;
    }
    .scene-layer-body {
      grid-column: 2;
      grid-row: 1;
      min-width: 0;
    }
    .scene-layer-status {
      grid-column: 2;
      grid-row: 2;
      min-width: 0;
    }
    .scene-layer-editor button {
      min-height: 26px;
      border: 1px solid rgba(126, 211, 255, 0.26);
      background: rgba(9, 18, 28, 0.82);
      color: #d9f3ff;
      font: 600 11px/1.1 ui-monospace, SFMono-Regular, Menlo, monospace;
      text-transform: uppercase;
      cursor: pointer;
    }
    .scene-layer-editor {
      display: grid;
      gap: 8px;
    }
    .scene-layer-header {
      display: grid;
      gap: 4px;
      min-width: 0;
    }
    .scene-layer-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .scene-layer-save-json {
      border: 1px solid rgba(126, 211, 255, 0.16);
      background: rgba(0, 0, 0, 0.16);
    }
    .scene-layer-save-json summary {
      display: flex;
      align-items: center;
      min-height: 30px;
      padding: 0 8px;
      cursor: pointer;
      color: rgba(217, 243, 255, 0.88);
      font: 700 11px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
      text-transform: uppercase;
      list-style: none;
    }
    .scene-layer-save-json summary::-webkit-details-marker {
      display: none;
    }
    .scene-layer-save-json summary::before {
      content: "›";
      margin-right: 8px;
      color: #ffcf86;
    }
    .scene-layer-save-json[open] summary::before {
      transform: rotate(90deg);
    }
    .scene-layer-save-json .scene-layer-actions {
      padding: 0 8px 8px;
    }
    .scene-layer-title {
      color: #ffcf86;
      font: 700 12px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
      text-transform: uppercase;
    }
    .scene-layer-file,
    .scene-layer-json,
    .scene-layer-status {
      font: 11px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    .scene-layer-file {
      color: rgba(217, 243, 255, 0.72);
      white-space: normal;
      word-break: break-all;
    }
    .scene-layer-json {
      max-height: 260px;
      overflow: auto;
      margin: 0;
      padding: 8px;
      color: rgba(217, 243, 255, 0.72);
      background: rgba(0, 0, 0, 0.28);
    }
    .scene-layer-json[hidden] { display: none; }
    .scene-layer-status[data-kind="ok"] { color: #9cff86; }
    .scene-layer-status[data-kind="error"] { color: #ff8888; }
  `;
  document.head.appendChild(style);
}
